import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { MenuType } from '~/components/Menu/menus';
import { menus } from '~/components/Menu/menus';
import { QUICK_ACCESS_MAX_SLOTS } from '~/constants/quickAccess';
import * as quickAccessService from '~/services/quickAccess';
import { selectors } from '~/store/modules';

export type QuickAccessItem = {
  key: string;
  title: string;
  parentTitle: string;
  icon?: ReactNode;
  menu: MenuType;
};

type FlattenParams = {
  items: MenuType[];
  parentTitle: string;
  rootIcon?: ReactNode;
};

const flattenMenus = ({ items, parentTitle, rootIcon }: FlattenParams): QuickAccessItem[] =>
  items.reduce<QuickAccessItem[]>((accumulator, item) => {
    const icon = rootIcon ?? item.icon;

    if (item.MenuItems?.length) {
      return [
        ...accumulator,
        ...flattenMenus({ items: item.MenuItems, parentTitle: item.title, rootIcon: icon }),
      ];
    }

    if (!item.component) return accumulator;

    return [...accumulator, { key: item.key, title: item.title, parentTitle, icon, menu: item }];
  }, []);

const resolveKeys = (keys: string[], catalog: Map<string, QuickAccessItem>): QuickAccessItem[] =>
  keys.reduce<QuickAccessItem[]>((accumulator, key) => {
    const item = catalog.get(key);
    if (!item) return accumulator;

    return [...accumulator, item];
  }, []);

export const useQuickAccess = () => {
  const permissions = useSelector(selectors.ui.auth.getPermissions);
  const profile = useSelector(selectors.ui.auth.getProfile);
  const userId = profile?.veiling_claim_type_identifier ?? '';

  const [pinnedKeys, setPinnedKeys] = useState<string[]>([]);
  const [recentKeys, setRecentKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!userId) return;

    setPinnedKeys(quickAccessService.getUserPins(userId));
    setRecentKeys(quickAccessService.getRecents(userId));
  }, [userId]);

  const options = useMemo(() => {
    const allowed = new Set(permissions?.map(permission => permission.permissionName) ?? []);

    return flattenMenus({ items: menus, parentTitle: '' }).filter(
      item => !!item.menu.permissionName && allowed.has(item.menu.permissionName),
    );
  }, [permissions]);

  const catalog = useMemo(() => new Map(options.map(item => [item.key, item])), [options]);

  const frequents = useMemo(() => {
    const curated = resolveKeys(quickAccessService.getDefaultShortcuts(), catalog);
    const curatedKeys = new Set(curated.map(item => item.key));
    const backfill = options.filter(item => !curatedKeys.has(item.key));

    return [...curated, ...backfill].slice(0, QUICK_ACCESS_MAX_SLOTS);
  }, [catalog, options]);

  const shortcuts = useMemo(() => {
    const layered = [...resolveKeys(pinnedKeys, catalog), ...frequents];

    const unique = new Map(layered.map(item => [item.key, item]));
    return [...unique.values()].slice(0, QUICK_ACCESS_MAX_SLOTS);
  }, [catalog, frequents, pinnedKeys]);

  const recents = useMemo(() => resolveKeys(recentKeys, catalog), [catalog, recentKeys]);

  const savePins = useCallback(
    (keys: string[]) => {
      const nextKeys = keys.slice(0, QUICK_ACCESS_MAX_SLOTS);
      quickAccessService.setUserPins(userId, nextKeys);
      setPinnedKeys(nextKeys);
    },
    [userId],
  );

  const registerAccess = useCallback(
    (menuKey: string) => {
      const nextRecents = quickAccessService.registerAccess(userId, menuKey);
      setRecentKeys(nextRecents);
    },
    [userId],
  );

  return {
    shortcuts,
    frequents,
    recents,
    options,
    pinnedKeys,
    savePins,
    registerAccess,
    hasPermissions: options.length > 0,
  };
};
